import { useState, useEffect, useRef, useCallback, useLayoutEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import Skeleton from '../../../components/ui/skeleton';
import Heading from '../../../components/Heading';
import useInsurance from '../../../stores/useInsurance';
import InsuranceCard from './InsuranceCard';
import { LanguageContext } from '../../../contexts/LanguageContext';

export function InsuranceSlider({ className = '' }) {
  const { t } = useTranslation();
  const { language } =
    useContext(LanguageContext) ||
    (() => {
      throw new Error('LanguageSwitcher must be used within a LanguageProvider');
    })();
  const lang = language === 'id';
  const { insurances, fetchLoading, hasFetched, fetchInsurances } = useInsurance();
  const [slidesPerView, setSlidesPerView] = useState(1.5);
  const [cardWidth, setCardWidth] = useState(300);
  const containerRef = useRef<HTMLDivElement>(null);
  const SERVICE_CARD_GAP = 8;

  const updateLayout = useCallback(() => {
    const width = window.innerWidth;
    const newCardWidth = 300;

    setCardWidth(newCardWidth);

    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const calculatedSlides = containerWidth / (newCardWidth + SERVICE_CARD_GAP);
      let slidesToSet;

      if (width < 300) slidesToSet = 1;
      else if (width < 400) slidesToSet = 1;
      else if (width < 620) slidesToSet = 1.2;
      else if (width < 700) slidesToSet = 2;
      else if (width < 980) slidesToSet = 2.2;
      else if (width > 980) slidesToSet = 3.5;
      else slidesToSet = Math.floor(calculatedSlides);

      setSlidesPerView(slidesToSet);
    }
  }, []);

  useLayoutEffect(() => {
    updateLayout();
  }, [updateLayout, insurances]);

  useEffect(() => {
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, [updateLayout]);

  useEffect(() => {
    if (!hasFetched) fetchInsurances();
  }, [fetchInsurances, hasFetched]);

  const skeletonSlides = Array.from({ length: 10 }, (_, i) => (
    <SwiperSlide key={`skeleton-${i}`} style={{ width: cardWidth }}>
      <Skeleton className={`shadow-md h-64 w-[${cardWidth}px]`} />
    </SwiperSlide>
  ));

  const insuranceSlides = insurances?.map((insurance) => (
    <SwiperSlide key={insurance.insurance_id} className="pb-12" style={{ width: cardWidth }}>
      <InsuranceCard
        name={insurance.name_ins}
        description={lang ? insurance.description_ins_id : insurance.description_ins_en || insurance.description_ins_id}
        logo={insurance.logo_ins || '/assets/no-data-image.svg'}
        className="w-full border border-border"
      />
    </SwiperSlide>
  ));

  return (
    <section
      ref={containerRef}
      className={
        insurances.length === 0
          ? 'hidden'
          : `${className} relative flex flex-col sm:text-md text-sm items-center gap-4 overflow-hidden text-center mt-4 md:mt-8`
      }
    >
      <div className="flex flex-col gap-1 mb-4 sm:text-base text-sm">
        <Heading>{t('tagline.detail-service.insurance.title')}</Heading>
        <p>{t('tagline.detail-service.insurance.tagline')}</p>
      </div>
      <div className="relative md:w-[90vw] w-full max-w-screen-xl">
        <Swiper
          modules={[Pagination, Autoplay]}
          autoplay={
            insurances.length >= slidesPerView
              ? { delay: 2500, disableOnInteraction: false, pauseOnMouseEnter: true }
              : false
          }
          pagination={{ clickable: true, dynamicBullets: true, dynamicMainBullets: 3 }}
          loop={insurances.length > slidesPerView}
          centerInsufficientSlides
          slidesPerView={slidesPerView}
          spaceBetween={SERVICE_CARD_GAP}
        >
          {fetchLoading ? skeletonSlides : insuranceSlides}
        </Swiper>
      </div>
    </section>
  );
}
